import React from 'react';
import { ArrowRightToLine, ChevronLeft, Share } from 'lucide-react';
import { Button } from '@cotera/client/v0/components/ui/button';
import {
  PageBarSidebarTrigger,
  SidebarTriggerClaimedProvider,
} from '@cotera/client/v0/components/ui/page-bar-sidebar-trigger';
// Direct module, not the `app/components` barrel: the barrel's import graph
// cycles back into this subtree, so `Tooltip` (a const) is still uninitialized
// when this module evaluates and renders as undefined.
import { Tooltip } from '@cotera/client/app/components/ui/tooltip';
import { UnifiedPanelBreadcrumbs } from './unified-panel-breadcrumbs';
import type { ArtifactDescriptor } from './artifacts';
import { shareableResourceForArtifact } from './artifacts/resource-artifact';
import { useArtifactTitle } from './use-artifact-title';
import { ArtifactPanelExpandButton } from './artifact-panel-expand-button';
import {
  PanelBarActionsSlotProvider,
  PanelBarTitleSlotProvider,
  UnifiedBarRenderedProvider,
  useArtifactPanel,
  usePanelUnifiedChrome,
} from './artifact-panel-context';

/**
 * The single bar shown at the top of every artifact in the panel, plus the
 * artifact content beneath it:
 *
 *   [‹] [icon] <artifact> ▾ⁿ › … › <pushView…>  ·  [actions] [⤢] [✕]
 *
 * Renders the artifact switcher and drill-in trail
 * ({@link UnifiedPanelBreadcrumbs}), the hosting artifact's own `actions` (a
 * resource's model selector, share button, …), the expand toggle, and a button
 * that dismisses the panel. Used both by {@link ResourcePageShell} (resource
 * artifacts) and by the panel's fallback for bare artifact types (charts,
 * workflow-node configs) so every artifact gets the same one bar.
 *
 * `children` is the content below the bar. It is rendered with the bar marked
 * as claimed — so a nested {@link ResourcePageShell} draws no second bar — and
 * with a portal target in scope so that nested shell can still put its own
 * actions in *this* bar instead of losing them.
 *
 * The bar is a container query context (`panel-bar`), which is how the trail
 * sheds its intermediate crumbs as the split narrows rather than at some
 * viewport width that says nothing about how wide this panel actually is.
 *
 * Drilled into an artifact's stack (Run details, Step details, …) it also leads
 * with a back arrow. That is not a duplicate of the trail: the trail jumps to a
 * named level and collapses under pressure, while the arrow is always exactly
 * one level up. `ResourcePageShell` drops its own `leading` slot whenever this
 * bar is in play, so this is the only back control in the panel.
 */
/**
 * The focus view's Share: this artifact, not the conversation that made it.
 *
 * Only here. In the split, the chat's bar sits above both panes and its Share
 * grants the conversation *and* every artifact in it as selectable rows, so a
 * second button would open the same dialog a few hundred pixels away. On a page
 * showing one artifact that superset is the wrong offer, and there is no other
 * Share on screen to defer to.
 *
 * Renders nothing for an artifact backed by no resource — a chart or a workflow
 * step has no id anyone can be granted.
 */
const ArtifactFocusViewShare: React.FC<{
  artifact: ArtifactDescriptor;
  onShare: (artifact: ArtifactDescriptor) => void;
}> = ({ artifact, onShare }) => {
  const title = useArtifactTitle(artifact);
  if (shareableResourceForArtifact(artifact) === null) {
    return null;
  }
  const label = `Share ${title}`;
  // Icon alone, like everything else in this group. Focus fills the pane with
  // one artifact and the bar above it is the only chrome left, so a filled
  // button with a word in it was the loudest thing on a screen meant for
  // reading — and the odd one out beside the glyphs it sits with. The name it
  // dropped is in the tooltip, which is where the rest of the bar keeps its
  // labels.
  return (
    <Tooltip side="bottom" tooltipContent={label}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className="size-8 shrink-0 p-0 hover:bg-sidebar-accent hover:text-sidebar-accent-foreground active:bg-sidebar-accent active:text-sidebar-accent-foreground"
        aria-label={label}
        onClick={() => onShare(artifact)}
      >
        <Share className="size-4" />
      </Button>
    </Tooltip>
  );
};

export const UnifiedPanelBar: React.FC<{
  actions?: React.ReactNode;
  /**
   * A control belonging to the artifact's *name*, drawn at the end of the
   * switcher and trail rather than in the actions cluster. See
   * {@link UnifiedPanelBreadcrumbs.trailing}.
   */
  titleAdornment?: React.ReactNode;
  children?: React.ReactNode;
}> = ({ actions, titleAdornment, children }) => {
  const chrome = usePanelUnifiedChrome();
  const artifactPanel = useArtifactPanel();
  const canExpand = artifactPanel?.canExpand === true;
  const hasActions = actions !== undefined && actions !== null;
  const viewDepth = chrome?.viewStack.length ?? 0;
  /**
   * The chevron, which pops one level off the artifact's own view stack.
   *
   * Leaving focus is deliberately not here. A chevron says you went somewhere
   * and are going back, and focus is not somewhere you went — the chat is still
   * mounted underneath, the address only names which artifact is filling it.
   * The toggle on the right undoes it, the same control that applied it, so
   * there is one exit rather than a chevron and a toggle a few pixels apart
   * disagreeing about what happened.
   */
  const back = ((): { label: string; onClick: () => void } | null => {
    if (chrome === null || viewDepth === 0) {
      return null;
    }
    return {
      label: 'Back',
      onClick: () => chrome.popToViewDepth(viewDepth - 1),
    };
  })();
  // Callback ref rather than a plain ref: the portal target has to be state so
  // the subtree re-renders once the node exists, otherwise a nested shell
  // mounting in the same commit reads `null` and drops its actions again.
  const [actionsSlot, setActionsSlot] = React.useState<HTMLDivElement | null>(
    null
  );
  // The trail's own slot, for a nested shell's title adornment. Same callback
  // ref reasoning as `actionsSlot` above.
  const [titleSlot, setTitleSlot] = React.useState<HTMLSpanElement | null>(
    null
  );
  const focusViewArtifact =
    chrome?.focusView === undefined
      ? null
      : chrome.artifacts.find(
          (candidate) => candidate.artifactId === chrome.activeArtifactId
        ) ?? null;

  return (
    <>
      <div
        data-slot="unified-panel-bar"
        className="@container/panel-bar sticky top-0 z-20 flex h-11 shrink-0 items-center gap-1 border-b border-border/50 bg-background px-3"
      >
        <PageBarSidebarTrigger />
        {back !== null ? (
          <Tooltip side="bottom" tooltipContent={back.label}>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="size-8 shrink-0 p-0"
              aria-label={back.label}
              onClick={back.onClick}
            >
              <ChevronLeft className="size-4" />
            </Button>
          </Tooltip>
        ) : null}
        <UnifiedPanelBreadcrumbs
          trailing={
            <>
              {titleAdornment}
              {/* `contents` so a nested shell's portalled star sits in the
                  trail's own row rather than inside a box of its own. */}
              <span ref={setTitleSlot} className="contents" />
            </>
          }
        />
        {/* One rhythm across the whole cluster. It used to run `gap-3` between
            the artifact's controls and the window's, and `gap-1` inside the
            window group — but a nested shell portals its actions in as
            `contents`, so each of them became a direct child on the outer gap.
            The model selector then sat 12px from the expand button while expand
            and dismiss sat 4px apart, and the row read as a wide gap with a
            tight pair stuck on the end. `gap-2` throughout keeps the 8px the
            workflow's bare save dot needs to stay clear of a filled Share,
            without singling out one seam. */}
        <div className="flex shrink-0 items-center gap-2">
          {hasActions ? actions : null}
          {/* `contents` so a nested shell's portalled actions sit in this flex
              row directly rather than inside a box of their own. Ahead of the
              window controls, so a shell that portals its actions in lands them
              beside the artifact's own rather than in the middle of Share and
              expand. */}
          <div ref={setActionsSlot} className="contents" />
          {/* The window's own controls. Share belongs here rather than with the
              artifact's actions: like expand and hide it reaches past what is on
              screen to the surface holding it, and left of a model selector it
              read as one more of the artifact's own affairs.

              Folds away entirely when it holds nothing, so the `gap-3` above
              does not leave a hole where the group would have been. */}
          <div
            data-slot="panel-bar-window-controls"
            className="flex shrink-0 items-center gap-2 [&:empty]:hidden"
          >
            {focusViewArtifact !== null && chrome?.focusView !== undefined ? (
              <ArtifactFocusViewShare
                artifact={focusViewArtifact}
                onShare={chrome.focusView.onShare}
              />
            ) : null}
            {chrome?.host?.accessControl ?? null}
            {canExpand || chrome?.focusView !== undefined ? (
              <ArtifactPanelExpandButton />
            ) : null}
            {chrome !== null && chrome.focusView === undefined ? (
              // Dismisses the panel; the artifact stays in the chat. Removing one
              // is the switcher menu's per-row close.
              //
              // Direction, not an `X`. An `X` reads as destructive, which is the
              // wrong promise for a button that only hides — and made it hard to
              // tell apart from the switcher's genuine remove.
              //
              // `ArrowRightToLine` pushes the panel out to the right edge, and
              // the pill that brings it back uses its mirror,
              // `ArrowLeftToLine`. The two being mirrors is the point: the panel
              // glyphs they replaced (`PanelRightClose`/`PanelRightOpen`) are
              // near-identical at 16px, so the pair could not tell you which way
              // the click went.
              <Tooltip side="bottom" tooltipContent="Hide panel">
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="size-8 shrink-0 p-0"
                  aria-label="Hide artifact panel"
                  onClick={chrome.closeActiveArtifact}
                >
                  <ArrowRightToLine className="size-4" />
                </Button>
              </Tooltip>
            ) : null}
          </div>
        </div>
      </div>
      <SidebarTriggerClaimedProvider value={true}>
        <UnifiedBarRenderedProvider value={true}>
          <PanelBarActionsSlotProvider value={actionsSlot}>
            <PanelBarTitleSlotProvider value={titleSlot}>
              {children}
            </PanelBarTitleSlotProvider>
          </PanelBarActionsSlotProvider>
        </UnifiedBarRenderedProvider>
      </SidebarTriggerClaimedProvider>
    </>
  );
};
